import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Button from '../components/Button';
import ErrorBoundary from '../components/ErrorBoundary';
import { LineChartComponent, BarChartComponent, PieChartComponent } from '../components/Chart';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const StatCard = ({ title, value, description }) => (
  <Card className="h-full">
    <div className="px-4 py-5 sm:p-6">
      <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
      <dd className="mt-1 text-3xl font-semibold text-gray-900">{value || 0}</dd>
      {description && (
        <dd className="mt-2 text-sm text-gray-500">{description}</dd>
      )}
    </div>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <ErrorBoundary>
    <Card>
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="h-80">
          {children}
        </div>
      </div>
    </Card>
  </ErrorBoundary>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalQuestions: 0,
    dailyQuestions: [],
    monthlyQuestions: [],
    activeSessions: 0,
    questionsByStatus: [],
    avgQuestionsPerSession: 0,
    sessions: []
  });
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          navigate('/admin/login');
          return;
        }

        const [statsResponse, feedbackResponse] = await Promise.all([
          api.get('/api/admin/stats', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          api.get('/api/feedback', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        // Ensure questionsByStatus is an array
        const questionsByStatus = Array.isArray(statsResponse.data.questionsByStatus) 
          ? statsResponse.data.questionsByStatus 
          : Object.entries(statsResponse.data.questionsByStatus || {}).map(([status, count]) => ({
              status,
              count
            }));

        setStats({
          totalQuestions: statsResponse.data.totalQuestions || 0,
          dailyQuestions: statsResponse.data.dailyQuestions || [],
          monthlyQuestions: statsResponse.data.monthlyQuestions || [],
          activeSessions: statsResponse.data.activeSessions || 0,
          questionsByStatus,
          avgQuestionsPerSession: statsResponse.data.avgQuestionsPerSession || 0,
          sessions: statsResponse.data.sessions || []
        });

        setFeedback(feedbackResponse.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/admin/login');
        } else {
          setError('Failed to fetch data');
          console.error('Error fetching data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Memoize chart data to prevent unnecessary re-renders
  const chartData = useMemo(() => ({
    dailyQuestions: stats.dailyQuestions,
    monthlyQuestions: stats.monthlyQuestions,
    questionsByStatus: stats.questionsByStatus
  }), [stats.dailyQuestions, stats.monthlyQuestions, stats.questionsByStatus]);

  const handleMarkAsRead = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.patch(`/api/feedback/${id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setFeedback(feedback.map(item => 
        item.id === id ? { ...item, status: newStatus } : item
      ));
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  if (loading) {
    return (
      <Layout title="Admin Dashboard">
        <div className="text-center py-8">Loading statistics...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Admin Dashboard">
        <div className="text-center py-8 text-red-600">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout title="Admin Dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Summary Statistics */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          <StatCard
            title="Total Questions"
            value={stats.totalQuestions}
            description="Total questions submitted across all sessions"
          />
          <StatCard
            title="Active Sessions"
            value={stats.activeSessions}
            description="Number of currently active sessions"
          />
          <StatCard
            title="Avg Questions per Session"
            value={Number(stats.avgQuestionsPerSession).toFixed(1)}
            description="Average number of questions per session"
          />
        </div>

        {/* Sessions List */}
        <ErrorBoundary>
          <Card className="mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">All Sessions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questions</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.sessions.map((session) => (
                      <tr key={session.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {session.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(session.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.question_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            session.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {session.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => navigate(`/session/${session.id}/display`)}
                            >
                              Display
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/session/${session.id}/manage`)}
                            >
                              Manage
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </ErrorBoundary>

        {/* Feedback Section */}
        <ErrorBoundary>
          <Card className="mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Feedback</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedback.map((item) => (
                      <tr key={item.id} className={item.status === 'unread' ? 'bg-blue-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.email}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.message}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.status === 'unread' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            size="sm"
                            variant={item.status === 'unread' ? 'primary' : 'secondary'}
                            onClick={() => handleMarkAsRead(item.id, item.status === 'unread' ? 'read' : 'unread')}
                          >
                            Mark as {item.status === 'unread' ? 'Read' : 'Unread'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </ErrorBoundary>

        {/* Charts Section */}
        <div className="space-y-8">
          {/* Daily Questions Chart */}
          {chartData.dailyQuestions.length > 0 && (
            <ChartCard title="Questions per Day (Last 30 Days)">
              <LineChartComponent data={chartData.dailyQuestions} />
            </ChartCard>
          )}

          {/* Monthly Questions Chart */}
          {chartData.monthlyQuestions.length > 0 && (
            <ChartCard title="Questions per Month">
              <BarChartComponent data={chartData.monthlyQuestions} />
            </ChartCard>
          )}

          {/* Questions by Status Chart */}
          {chartData.questionsByStatus.length > 0 && (
            <ChartCard title="Questions by Status">
              <PieChartComponent data={chartData.questionsByStatus} />
            </ChartCard>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard; 